import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import {
  AlertCircle,
  Loader2,
  Building2,
  CheckCircle,
  Eye,
  EyeOff,
  Circle,
} from 'lucide-react';

import { BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { registerSchema, RegisterFormData } from '../../utils/validations';
import LegalDialog from './LegalDialog';
import {
  fieldClassName,
  primaryButtonClassName,
  passwordToggleClassName,
  linkClassName,
} from './authStyles';

function RegistrationField({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-medium text-gray-700"
      >
        {label} <span className="text-red-500">*</span>
      </label>
      {children}
      {error && (
        <p
          id={`${id}-error`}
          className="mt-2 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}
interface AssociadoData {
  data_nascimento?: string | null;
  nome?: string | null;
  email?: string | null;
  empresa_nome?: string | null;
  empresa_codigo?: string | null;
  celular?: string | null;
  autorizado: boolean;
  message?: string | null;
  genero?: string | null;
  estado?: string | null;
  isAppleTest?: boolean;
}

const formatCPF = (value: string) => {
  const cleaned = value.replace(/\D/g, '');
  const part1 = cleaned.substring(0, 3);
  const part2 = cleaned.substring(3, 6);
  const part3 = cleaned.substring(6, 9);
  const part4 = cleaned.substring(9, 11);

  if (cleaned.length <= 3) return part1;
  if (cleaned.length <= 6) return `${part1}.${part2}`;
  if (cleaned.length <= 9) return `${part1}.${part2}.${part3}`;
  return `${part1}.${part2}.${part3}-${part4}`;
};

const hasValue = (value?: string | null): boolean =>
  Boolean(value && value.trim().length > 0);

export function RegisterForm({
  onSuccess,
  onSwitchToLogin,
}: RegisterFormProps): ReactElement {
  const { signUp } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openTermos, setOpenTermos] = useState(false);
  const [openPrivacidade, setOpenPrivacidade] = useState(false);

  //Estado para mostrar senha
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  //Estados para validação do CPF
  const [cpfValidationState, setCpfValidationState] = useState<{
    loading: boolean;
    validated: boolean;
    error: string;
    data: AssociadoData | null;
  }>({
    loading: false,
    validated: false,
    error: '',
    data: null,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      termos: false,
      ofertas: false,
      sms: false,
      origem: '',
      grupo: '',
      empresa: '',
    },
  });

  const cpfValue = watch('cpf') || '';
  const senha = watch('senha') || '';

  useEffect(() => {
    const cpfLimpo = cpfValue.replace(/\D/g, '');

    if (cpfLimpo.length < 11) {
      setCpfValidationState({
        loading: false,
        validated: false,
        error: '',
        data: null,
      });
      return;
    }

    if (cpfLimpo.length === 11) {
      validarAssociado(cpfLimpo);
    }
  }, [cpfValue]);

  const validarAssociado = async (cpf: string) => {
    setCpfValidationState({
      loading: true,
      validated: false,
      error: '',
      data: null,
    });
    try {
      const response = await axios.get(
        `${BASE_URL}/club/validarAssociado?cpf=${cpf}`,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000,
        },
      );

      const associadoData: AssociadoData = response.data;

      if (associadoData.autorizado) {
        setValue('nome', associadoData.nome || '', { shouldValidate: true });
        setValue('genero', associadoData.genero || '', {
          shouldValidate: true,
        });
        setValue('dataNascimento', associadoData.data_nascimento || '', {
          shouldValidate: true,
        });
        setValue('estado', associadoData.estado || '', {
          shouldValidate: true,
        });
        setValue('email', associadoData.email || '', { shouldValidate: true });
        setValue('celular', associadoData.celular || '', {
          shouldValidate: true,
        });
        setValue('confirmarEmail', associadoData.email || '', {
          shouldValidate: true,
        });

        if (associadoData.empresa_nome) {
          setValue('empresa', associadoData.empresa_nome, {
            shouldValidate: true,
          });
        }

        if (associadoData.isAppleTest) {
          clearErrors('cpf');
        }

        // setValue('senha', 'AppleTest@2024', { shouldValidate: true });
        // setValue('confirmarSenha', 'AppleTest@2024', { shouldValidate: true });
        clearErrors('cpf');
        clearErrors('senha');
        clearErrors('confirmarSenha');

        clearErrors('cpf');

        setCpfValidationState({
          loading: false,
          validated: true,
          error: '',
          data: associadoData,
        });
      } else {
        const errorMsg =
          associadoData.message ||
          'CPF não autorizado para cadastro no Clube de Benefícios';

        setCpfValidationState({
          loading: false,
          validated: false,
          error: errorMsg,
          data: null,
        });

        setError('cpf', {
          type: 'manual',
          message: errorMsg,
        });
      }
    } catch (error: any) {
      console.error('Erro na validação do CPF:', error.response);

      let errorMsg = 'Erro ao validar CPF. Tente novamente.';

      if (error.response?.status === 404) {
        errorMsg = 'CPF não encontrado ou não autorizado para cadastro';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data.mensagem || 'CPF inválido';
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'Timeout na validação. Verifique sua conexão.';
      } else if (
        error.response?.data?.mensagem ||
        error.response?.data?.message
      ) {
        errorMsg = error.response.data.mensagem || error.response.data.message;
      }

      setCpfValidationState({
        loading: false,
        validated: false,
        error: errorMsg,
        data: null,
      });

      setError('cpf', {
        type: 'manual',
        message: errorMsg,
      });
    }
  };

  const hasUppercase = /[A-Z]/.test(senha);
  const hasLowercase = /[a-z]/.test(senha);
  const hasNumber = /[0-9]/.test(senha);
  const hasSpecialChar = /[!@#$%^&*]/.test(senha);
  const hasQuantity = senha.length > 5;
  const passwordRequirements = [
    { label: '6 caracteres ou mais', met: hasQuantity },
    { label: 'Letra maiúscula', met: hasUppercase },
    { label: 'Letra minúscula', met: hasLowercase },
    { label: 'Número', met: hasNumber },
    { label: 'Caractere especial (!@#$%^&*)', met: hasSpecialChar },
  ];
  const fieldAttributes = (name: keyof RegisterFormData) => ({
    id: `clube-${name}`,
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `clube-${name}-error` : undefined,
    className: fieldClassName,
  });

  const onSubmit = async (data: RegisterFormData) => {
    if (!cpfValidationState.validated) {
      setErrorMessage('CPF deve ser validado antes do cadastro');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const dadosParaBackend = {
        nome: data.nome,
        cpf: data.cpf?.replace(/\D/g, ''),
        genero: data.genero,
        celular: data.celular?.replace(/\D/g, ''),
        data_nascimento: data.dataNascimento,
        estado: data.estado,
        aceite_termos: data.termos ? 1 : 0,
        aceite_ofertas: data.ofertas ? 1 : 0,
        aceite_sms: data.sms ? 1 : 0,
        empresa: data.empresa || cpfValidationState.data?.empresa_nome || '',
        empresa_codigo: cpfValidationState.data?.empresa_codigo || '',
        observacoes: `Cadastro via Clube de Benefícios - Associado validado`,
      };

      await signUp(data.email, data.senha, dadosParaBackend);

      onSuccess?.();
      navigate('/ClubeBeneficios', { replace: true }); // Redireciona para a página de login
    } catch (error: any) {
      console.error('Erro no cadastro:', error);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          'Erro ao realizar cadastro. Tente novamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-w-0">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Crie sua conta</h1>
        <p className="mt-2 text-sm text-gray-500">
          Campos com <span className="text-red-500">*</span> são obrigatórios
        </p>
      </header>

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
        >
          <AlertCircle
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
            aria-hidden="true"
          />
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <fieldset className="min-w-0 space-y-5">
          <legend className="text-base font-semibold text-gray-900">
            Dados pessoais
          </legend>
          <div className="grid grid-cols-1 items-start gap-x-6 md:grid-cols-2">
            <RegistrationField id="clube-cpf" label="CPF">
              <div className="relative">
                <input
                  {...register('cpf')}
                  {...fieldAttributes('cpf')}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="000.000.000-00"
                  maxLength={14}
                  aria-describedby="clube-cpf-status"
                  aria-invalid={Boolean(cpfValidationState.error || errors.cpf)}
                  className={`${fieldClassName} pr-10 ${
                    cpfValidationState.validated ? 'border-green-500' : ''
                  }`}
                  onChange={e => {
                    const formatted = formatCPF(e.target.value);
                    setValue('cpf', formatted, { shouldValidate: true });
                  }}
                />
                <div
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                  aria-hidden="true"
                >
                  {cpfValidationState.loading && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-main" />
                  )}
                  {cpfValidationState.validated && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                  {cpfValidationState.error && (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
              </div>
            </RegistrationField>
            <div
              id="clube-cpf-status"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="min-h-[88px] min-w-0 py-3 text-sm md:min-h-[112px] md:pt-8"
            >
              {cpfValidationState.loading ? (
                <p className="text-gray-600">Validando CPF…</p>
              ) : cpfValidationState.error || errors.cpf ? (
                <p className="break-words text-red-600">
                  {cpfValidationState.error || errors.cpf?.message}
                </p>
              ) : cpfValidationState.validated ? (
                <div className="space-y-1">
                  <p className="font-medium text-green-700">CPF habilitado</p>
                  {hasValue(cpfValidationState.data?.empresa_nome) && (
                    <p className="flex items-start gap-2 break-words text-gray-600">
                      <Building2
                        className="mt-0.5 h-4 w-4 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span
                        className="min-w-0"
                        title={
                          cpfValidationState.data?.empresa_nome || undefined
                        }
                      >
                        {cpfValidationState.data?.empresa_nome}
                      </span>
                    </p>
                  )}
                  {cpfValidationState.data?.isAppleTest && (
                    <p className="text-gray-500">Conta de teste Apple</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <RegistrationField
            id="clube-nome"
            label="Nome completo"
            error={errors.nome?.message}
          >
            <input
              {...register('nome')}
              {...fieldAttributes('nome')}
              type="text"
              autoComplete="name"
              placeholder="Digite seu nome completo"
            />
          </RegistrationField>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <RegistrationField
              id="clube-genero"
              label="Gênero"
              error={errors.genero?.message}
            >
              <select {...register('genero')} {...fieldAttributes('genero')}>
                <option value="">Selecione...</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="O">Outro</option>
              </select>
            </RegistrationField>
            <RegistrationField
              id="clube-dataNascimento"
              label="Data de nascimento"
              error={errors.dataNascimento?.message}
            >
              <input
                {...register('dataNascimento')}
                {...fieldAttributes('dataNascimento')}
                type="date"
                autoComplete="bday"
              />
            </RegistrationField>
            <RegistrationField
              id="clube-estado"
              label="Estado"
              error={errors.estado?.message}
            >
              <select
                {...register('estado')}
                {...fieldAttributes('estado')}
                autoComplete="address-level1"
              >
                <option value="">Escolha um estado</option>
                <option value="AC">Acre</option>
                <option value="AL">Alagoas</option>
                <option value="AP">Amapá</option>
                <option value="AM">Amazonas</option>
                <option value="BA">Bahia</option>
                <option value="CE">Ceará</option>
                <option value="DF">Distrito Federal</option>
                <option value="ES">Espírito Santo</option>
                <option value="GO">Goiás</option>
                <option value="MA">Maranhão</option>
                <option value="MT">Mato Grosso</option>
                <option value="MS">Mato Grosso do Sul</option>
                <option value="MG">Minas Gerais</option>
                <option value="PA">Pará</option>
                <option value="PB">Paraíba</option>
                <option value="PR">Paraná</option>
                <option value="PE">Pernambuco</option>
                <option value="PI">Piauí</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="RN">Rio Grande do Norte</option>
                <option value="RS">Rio Grande do Sul</option>
                <option value="RO">Rondônia</option>
                <option value="RR">Roraima</option>
                <option value="SC">Santa Catarina</option>
                <option value="SP">São Paulo</option>
                <option value="SE">Sergipe</option>
                <option value="TO">Tocantins</option>
              </select>
            </RegistrationField>
          </div>
        </fieldset>

        <fieldset className="min-w-0 border-t border-gray-200 pt-6">
          <legend className="pr-3 text-base font-semibold text-gray-900">
            Contato
          </legend>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <RegistrationField
              id="clube-email"
              label="E-mail"
              error={errors.email?.message}
            >
              <input
                {...register('email')}
                {...fieldAttributes('email')}
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
              />
            </RegistrationField>
            <RegistrationField
              id="clube-confirmarEmail"
              label="Confirmar e-mail"
              error={errors.confirmarEmail?.message}
            >
              <input
                {...register('confirmarEmail')}
                {...fieldAttributes('confirmarEmail')}
                type="email"
                autoComplete="off"
                placeholder="Confirme seu e-mail"
              />
            </RegistrationField>
            <RegistrationField
              id="clube-celular"
              label="Celular"
              error={errors.celular?.message}
            >
              <input
                {...register('celular')}
                {...fieldAttributes('celular')}
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="11999999999"
                maxLength={11}
                value={watch('celular') || ''}
                onChange={e => {
                  setValue('celular', e.target.value.replace(/\D/g, ''));
                }}
              />
            </RegistrationField>
          </div>
        </fieldset>

        <fieldset className="min-w-0 border-t border-gray-200 pt-6">
          <legend className="pr-3 text-base font-semibold text-gray-900">
            Acesso
          </legend>
          <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
            <RegistrationField
              id="clube-senha"
              label="Senha"
              error={errors.senha?.message}
            >
              <div className="relative">
                <input
                  {...register('senha')}
                  {...fieldAttributes('senha')}
                  className={`${fieldClassName} pr-12`}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Digite sua senha"
                  aria-describedby={
                    errors.senha
                      ? 'clube-senha-error clube-senha-requisitos'
                      : 'clube-senha-requisitos'
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-controls="clube-senha"
                  aria-pressed={showPassword}
                  className={passwordToggleClassName}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </RegistrationField>
            <RegistrationField
              id="clube-confirmarSenha"
              label="Confirmar senha"
              error={errors.confirmarSenha?.message}
            >
              <div className="relative">
                <input
                  {...register('confirmarSenha')}
                  {...fieldAttributes('confirmarSenha')}
                  className={`${fieldClassName} pr-12`}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirme sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword
                      ? 'Ocultar confirmação de senha'
                      : 'Mostrar confirmação de senha'
                  }
                  title={
                    showConfirmPassword
                      ? 'Ocultar confirmação de senha'
                      : 'Mostrar confirmação de senha'
                  }
                  aria-controls="clube-confirmarSenha"
                  aria-pressed={showConfirmPassword}
                  className={passwordToggleClassName}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </RegistrationField>
          </div>
          <ul
            id="clube-senha-requisitos"
            aria-label="Requisitos da senha"
            className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 bg-gray-50 px-4 py-3 text-xs sm:grid-cols-2"
          >
            {passwordRequirements.map(requirement => (
              <li
                key={requirement.label}
                className={`flex min-w-0 items-start gap-2 ${
                  requirement.met ? 'text-green-700' : 'text-gray-600'
                }`}
              >
                {requirement.met ? (
                  <CheckCircle
                    className="h-4 w-4 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <Circle
                    className="h-4 w-4 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>
                  {requirement.label}
                  <span className="sr-only">
                    {requirement.met ? ': atendido' : ': pendente'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </fieldset>

        {/* Termos e Condições */}
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <h2 className="text-base font-semibold text-gray-900">
            Termos e preferências
          </h2>

          <label className="flex items-start gap-3">
            <input
              {...register('termos')}
              type="checkbox"
              className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-primary-main focus:ring-primary-main"
            />
            <span className="text-sm text-gray-700">
              Aceito os{' '}
              <button
                type="button"
                className="font-medium text-primary-main underline underline-offset-2 hover:text-primary-dark focus-visible:outline-primary-main"
                onClick={() => setOpenTermos(true)}
              >
                Termos de Uso
              </button>{' '}
              e{' '}
              <button
                type="button"
                className="font-medium text-primary-main underline underline-offset-2 hover:text-primary-dark focus-visible:outline-primary-main"
                onClick={() => setOpenPrivacidade(true)}
              >
                Política de Privacidade
              </button>
              <span className="text-red-500">*</span>
            </span>
          </label>

          {errors.termos && (
            <p className="text-sm text-red-600">{errors.termos.message}</p>
          )}

          {/* Modal Termos de Uso */}
          {openTermos && (
            <LegalDialog
              title="Termos de Uso"
              onClose={() => setOpenTermos(false)}
            >
              <h3>I. FINALIDADE</h3>
              <p>
                Este termo regulamenta a utilização do Clube Ben e estabelece as
                competências e campo de atuação referente ao mesmo.
              </p>
              <h3>II. DEFINIÇÕES E ABREVIATURAS</h3>
              <p>
                Usuário: Clientes, empregados e estagiários que trabalhem na
                empresa ou em outras empresas vinculadas com contrato ativo com
                a Proxy Media Informação e Comunicação Ltda. Clube de Vantagens
                Clube Ben: Programa de benefícios e vantagens criado e
                administrado pela empresa Proxy Media Informação e Comunicação
                Ltda de uso exclusivo do usuário. Rede Conveniada: Conjunto de
                estabelecimentos comerciais, pessoas jurídicas cadastradas junto
                a Proxy Media Informação e Comunicação Ltda que viabilizam o
                clube de vantagens Clube Ben. Esses estabelecimentos podem ser
                lojas físicas ou on-line. Transação: Condições comerciais
                exclusivas oferecidas pelos estabelecimentos da rede conveniada
                e, de sua responsabilidade, visando o relacionamento e
                fidelização do usuário. O usuário que desejar conhecer os
                descontos ou condições exclusivas oferecidas por um dos
                parceiros, deverá acessar o hotsite do clube de vantagens Clube
                Ben, disponível no link divulgado. Para o caso das lojas
                físicas, para obter o desconto, o usuário deverá apresentar no
                parceiro selecionado; o cupom impresso (gerado pela plataforma)
                ou o crachá da empresa, e um documento identificado com foto.
              </p>
              <h3>III. PRINCÍPIOS BÁSICOS</h3>
              <h3>Considerações Gerais</h3>
              <p>
                1.1 O Termo de Uso deste site rege os serviços oferecidos pelo
                clube de vantagens Clube Ben, cuja utilização pressupõe a mais
                alta compreensão e vinculação de todas as regras aqui
                estabelecidas.
              </p>
              <h3>Objeto</h3>
              <p>
                2.1 Produtos e/ou serviços serão anunciados no site, em nome do
                parceiro, juntamente com as respectivas regras, destaques e
                informações necessárias ao usuário quanto aos requisitos para
                aquisição dos mesmos, por período determinado ou não, a variar
                conforme o caso.
              </p>
              <h3>Responsabilidade</h3>
              <p>
                3.1 O clube de vantagens Clube Ben não é o proprietário dos
                produtos e/ou serviços anunciados pelos parceiros, não tem sua
                posse e não realiza as promoções em seu nome, tampouco intervém
                na entrega e/ou uso dos produtos e/ou serviços.
              </p>
              <p>
                3.2 O clube de vantagens Clube Ben é responsável pela divulgação
                das promoções e descontos no site e outros meios de comunicação
                interna, não fazendo parte da cadeia de consumo ou fornecimento
                do produto e/ou serviço adquirido.
              </p>
              <p>
                3.3 Muito embora o clube de vantagens Clube Ben adote uma
                política de segurança e pretenda tomar todas as providências,
                dentro do razoável, para prevenir a introdução de vírus ou
                outros materiais destrutivos no site dos programas, não garante
                o acesso ininterrupto ou livre de erros em seu site na web, não
                assumindo qualquer responsabilidade por danos causados em
                virtude do acesso ao site ou por impossibilidade de acessá-lo.
                Qualquer tentativa de violar os sistemas e/ou banco de dados do
                clube de vantagens Clube Ben importará, além das sanções aqui
                previstas por violação deste Regulamento, em ações judiciais
                cabíveis e indenizações por eventuais danos causados.
              </p>
              <p>
                3.4 Em nenhuma oportunidade, qualquer das empresas com as quais
                o clube de vantagens Clube Ben mantenha associação corporativa
                incluindo a participação sobre os direitos de marca, marketing,
                desenvolvimento conjunto, relacionamento comercial ou de
                fornecimento, será responsável perante o participante por falha
                de desempenho do clube de vantagens. Em nenhum caso, o clube de
                vantagens Clube Ben e/ou seus controladores, seus diretores e
                agentes, serão responsáveis por quaisquer danos, reclamações ou
                perdas sofridas, incluindo, mas sem limitações, aqueles
                relacionados: ao programa/clube; a qualquer falha, atraso ou
                decisão na administração do programa/clube de vantagens; a
                utilização ou impossibilidade de utilizar o site do clube de
                vantagens “Clube Ben” na web; a compra ou uso de qualquer
                produto ou serviço oferecido pelos parceiros e/ou fornecedores,
                mesmo que o clube de vantagens “Clube Ben” e/ou seus
                representantes sejam advertidos da possível ocorrência de tais
                danos ao adquirir um produto e/ou serviço, o usuário declara
                conhecer as condições de uso, pagamento, recebimento e prazo de
                validade destes.
              </p>
              <h3>Penalidades</h3>
              <p>
                4.1 O usuário que infringir a legislação aplicável ou os
                compromissos aqui assumidos estará sujeito a sanções previstas
                neste Termo de Uso, não se escusando de responder a eventuais
                ações cíveis e penais pelas suas condutas.
              </p>
              <h3>Propriedade intelectual</h3>
              <p>
                5.1 O uso comercial da expressão Clube Ben como marca, nome
                empresarial ou nome de domínio, bem como os conteúdos das telas
                relativas aos serviços do clube de vantagens, assim como os
                programas, bancos de dados, redes e arquivos, são propriedade do
                clube de vantagens Clube Ben e estão protegidos pelas leis e
                tratados internacionais de direito autoral, marcas, patentes,
                modelos e desenhos industriais. O uso indevido e a reprodução
                total ou parcial dos referidos conteúdos são proibidos, salvo
                autorização expressa do clube de vantagens Clube Ben.
              </p>
              <h3>Cancelamento e Alteração do Programa</h3>
              <p>
                6.1 O clube de vantagens Clube Ben reserva-se o direito de
                cancelar o programa a qualquer momento, independente de
                notificação prévia. Além disso, reserva-se o direito de alterar
                o presente acordo a qualquer momento, no todo ou em parte,
                independente de notificação prévia, sendo que tais modificações
                serão presumidas como aceitação tácita das novas regras pelos
                participantes do Programa/Clube.
              </p>
            </LegalDialog>
          )}

          {/* Modal Política de Privacidade */}
          {openPrivacidade && (
            <LegalDialog
              title="Política de Privacidade"
              onClose={() => setOpenPrivacidade(false)}
            >
              <h3>Política de Privacidade Clube Ben – Proxy Media</h3>
              <p>
                Leia essa Política de Privacidade para entender nossas políticas
                sobre seus Dados Pessoais e a forma como os trataremos. A Proxy
                Media se preocupa com a sua privacidade e a respeita. Esta
                política de privacidade explica quais tipos de informações
                coletamos por meio de nossos sites, como essas informações serão
                utilizadas, e com quem essas informações serão compartilhadas,
                visando a total transparência entre a Proxy Media e você,
                usuário, em consonância com a Lei nº 13.709/18.
              </p>
              <h3>1 - QUAIS SÃO AS INFORMAÇÕES QUE COLETAMOS?</h3>
              <p>
                a. A Proxy Media coleta, registra e armazena apenas as
                informações fornecidas e consentidas por você em nossos sites. A
                autenticidade dos dados fornecidos é de extrema importância e
                depende somente de você, portanto, verifique seus dados antes de
                nos enviar. Assim, você é o responsável exclusivo pelas
                declarações prestadas em nossos sites e que vierem a causar
                prejuízos a si mesmo, à Proxy Media e/ou a terceiros, podendo
                corrigi-los ou alterá-los a qualquer tempo.
              </p>
              <p>
                b. O usuário que não desejar mais receber mensagens através dos
                domínios remetentes de nossos sites, nos termos do item 1.a,
                deverá enviar um email para o endereço
                contatoLGPD@clubeben.com.br, manifestando seu interesse e terá
                automaticamente seu cadastro eliminado do nosso banco de dados.
              </p>
              <p>
                c. Caso suspeite de qualquer uso indevido dos seus dados, não
                deixe de entrar em contato conosco, por meio do campo de contato
                do item 1.b.
              </p>
              <p>
                d. Caso deseje acessar seus dados, alterá-los e receber
                informações sobre eventuais compartilhamentos, entrar em contato
                conosco, por meio do campo de contato do item 1.b.
              </p>
              <h3>2 - PARA QUAL FINALIDADE OS DADOS SÃO UTILIZADOS?</h3>
              <p>
                O usuário ao acessar nossos sites, permite que a Proxy Media no
                papel de gestora, tenha a liberdade para utilizá-los para o
                envio de mensagens de cunho publicitário, novidades, ofertas e
                promoções da promotora, através dos domínios remetentes de
                nossos sites, bem como o envio de ofertas de produtos e/ou
                serviços dos nossos parceiros, sendo o usuário, aceitando os
                termos da política de privacidade e condições dos nossos
                parceiros, passando você a se submeter à política de privacidade
                da aplicação acessada.
              </p>
              <h3>3 - SEUS DIREITOS</h3>
              <p>
                A Proxy Media disponibiliza a você, mediante solicitação, a
                possibilidade de consulta, correção, atualização, portabilidade,
                exclusão, além da possibilidade de se obter informações sobre
                compartilhamento e anonimização de suas informações pessoais,
                mantidas no nosso Banco de Dados. Caso você deseje exercer seus
                direitos em relação às suas informações pessoais ou caso tenha
                alguma dúvida sobre como usamos suas informações pessoais, entre
                em contato conosco através dos seguintes canais: E-mail:
                contatoLGPD@proxymedia.com.br Telefone: (11) 2825-0790 Endereço:
                Rua Tito, 678 – cj 104, 105, 106, Bairro: Lapa Cidade/UF: São
                Paulo – SP CEP: 05051-000
              </p>
              <h3>4 – COOKIES</h3>
              <p>
                A Proxy Media assegura que não usufrui de nenhuma informação
                pessoal via cookies em nossos sites. Toda e qualquer informação
                armazenada pelo banco de dados da Proxy Media é fornecida pelo
                usuário, com seu expresso consentimento.
              </p>
              <h3>5- LOG DE ACESSO</h3>
              <p>
                A Proxy Media realiza a coleta e guarda dos dados sobre o seu
                acesso em nossos sites, conforme determinação legal, e mantém
                seus dados arquivados até que estes atinjam os fins a que se
                destinam, podendo o usuário solicitar tais informações a
                qualquer momento.
              </p>
              <h3>6 - COMO OS DADOS COMPARTILHADOS SÃO PROTEGIDOS?</h3>
              <p>
                A Política de Privacidade da Proxy Media, visa garantir que
                quaisquer informações e dados dos usuários não serão fornecidos,
                publicados ou comercializados em quaisquer circunstância, assim,
                tomamos as medidas de segurança e de proteção de dados
                necessárias para a evitar riscos de perda, mau uso, alteração,
                acessos não autorizados ou subtração dos dados por você
                compartilhados. Nossas medidas de segurança estão sendo
                melhoradas constantemente, de acordo com o desenvolvimento
                tecnológico. Ao aceitar a política de privacidade o usuário se
                submete também à política de privacidade da empresa promotora do
                clube.
              </p>
              <h3>7 - AUTORIDADE PÚBLICA</h3>
              <p>
                A Proxy Media somente disponibilizará seus dados às Autoridades
                Públicas mediante ordem judicial expressa, e para tanto for
                feito nos moldes da legislação e que iremos fornecer suas
                informações para a autoridade pública competente. Não efetuamos
                essa entrega de forma excessiva, ou quando não estiver em
                conformidade com as determinações legais.
              </p>
              <h3>8 - MENORES DE 12 ANOS</h3>
              <p>
                A Proxy Media não armazena, coleta ou trata dados de crianças
                menores de 13 (treze) anos de idade. Assim, se você é menor de
                13 (treze) anos, não poderá se cadastrar em nossos sites,
                enviando-nos informações pessoais. Se necessário, deverá
                solicitar ao seu responsável legal que realize o cadastro em
                nossos sites, com consentimento explicito daquela pessoa, e
                represente-o junto a este site.
              </p>
              <h3>9 - OUTRAS APLICAÇÕES</h3>
              <p>
                Quando você acessar plataformas e aplicativos de terceiros, a
                partir da navegação por meio dos nossos sites (ex. links do
                facebook, linkedin, youtube e instagram), você usuário passa a
                se submeter à política de privacidade da aplicação acessada, de
                modo que a presente política não se aplica aos dados pessoais
                coletados ou por você compartilhados nessas aplicações. A Proxy
                Media garante a privacidade dos dados pessoais do usuário
                coletados dentro da sua plataforma.
              </p>
              <h3>10 - ALTERAÇÕES A ESTA POLÍTICA DE PRIVACIDADE</h3>
              <p>
                a) - A presente política de privacidade está sujeita a
                alterações regulares para garantir que esteja atualizada com o
                uso de suas informações pessoais e em conformidade com as leis
                de proteção de dados aplicáveis. Por favor, verifique com
                frequência esta Política de Privacidade para se informar sobre
                possíveis alterações que possam vir a ser feitas.
              </p>
              <p>
                b) - A Proxy Media reserva-se o direito de alterar esta política
                de privacidade, de tempos em tempos, seja pela utilização de
                novas tecnologias ou sempre que a Proxy Media entender
                necessário, de acordo com sua política de Compliance. A política
                de privacidade atualizada será publicada em nosso site, bem como
                todos os usuários serão informados de eventuais alterações.
              </p>
              <h3>11 - LEI GERAL DE PROTEÇÃO DE DADOS</h3>
              <p>
                A Proxy Media é uma das primeiras Empresas de Marketing Digital
                a buscar adequação em conformidade com a Lei Geral de Proteção
                de Dados, que entrará em vigor em 01/08/2021. A Proxy Media
                garante que seus fornecedores, clientes e parceiros estejam
                também alinhados à Lei Geral de Proteção de Dados, trabalhando
                sempre com informações públicas ou com consentimento do usuário.
                A Proxy Media está reestruturando seus processos internos de
                armazenamento e controle de dados de seus usuários, adequando-se
                às exigências determinadas pela Lei Geral de Proteção de dados,
                que entrará em vigor em 01/08/2021. Para efeito de entendimento,
                a Proxy Media se enquadra às figuras de “Controlador”, possuidor
                dos dados e tomador de decisões a respeito do tratamento dos
                dados pessoais a depender da atividade que estivemos
                desenvolvendo para os nossos clientes.
              </p>
            </LegalDialog>
          )}

          <label className="flex items-start gap-3">
            <input
              {...register('ofertas')}
              type="checkbox"
              className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-primary-main focus:ring-primary-main"
            />
            <span className="text-sm text-gray-700">
              Desejo receber ofertas e promoções por e-mail
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              {...register('sms')}
              type="checkbox"
              className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-primary-main focus:ring-primary-main"
            />
            <span className="text-sm text-gray-700">
              Desejo receber ofertas e promoções por SMS
            </span>
          </label>
        </div>

        {/* Botão de submissão */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={
              isSubmitting ||
              !cpfValidationState.validated ||
              cpfValidationState.loading
            }
            className={primaryButtonClassName}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Cadastrando...
              </>
            ) : (
              'Cadastrar'
            )}
          </button>
        </div>

        {onSwitchToLogin && (
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-gray-600">
              Já tem uma conta?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className={linkClassName}
              >
                Fazer login
              </button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}

export default RegisterForm;
